/**
 * The BFF's wire surface, in one place.
 *
 * Everything the browser knows about authentication now lives behind these four URLs and two cookie
 * names. There is no client library, no token, and no storage: the session is an `HttpOnly` cookie the
 * browser sends on its own, and the only thing this file can actually read is the antiforgery token —
 * which is readable on purpose (see `XSRF_COOKIE_NAME`).
 *
 * Same-origin, always. The ingress routes `/bff`, `/signin-oidc`, `/api` and `/hubs` on this host through
 * to the orchestrator, so every path here is relative and the cookie is scoped to this host and nothing
 * else. A cross-origin arrangement would need `Access-Control-Allow-Credentials` and a cookie on a parent
 * domain; that was weighed and rejected (checklist D5).
 */

/** Where the browser goes to start a sign-in. Anonymous and reachable, so its `returnUrl` is validated server-side. */
export const BFF_LOGIN_PATH = '/bff/login'

/** Ends the session here and at the identity provider. `POST`, so it carries the CSRF header. */
export const BFF_LOGOUT_PATH = '/bff/logout'

/** Who the browser is signed in as. 401 means signed out — an answer, not a failure. */
export const BFF_USER_PATH = '/bff/user'

/** Forces a refresh of the server-side token bundle. The client calls it once after a 401, then gives up. */
export const BFF_REFRESH_PATH = '/bff/refresh'

/**
 * The antiforgery cookie, deliberately *not* `HttpOnly`: the double-submit pattern needs the SPA to read
 * it and echo it back. That readability is the mechanism, not an oversight — it carries no authority on
 * its own, and the server compares it against the value held with the session.
 */
export const XSRF_COOKIE_NAME = 'XSRF-TOKEN'

/** The header the value is echoed in. A mismatch on a state-changing request is a 403. */
export const CSRF_HEADER_NAME = 'X-CSRF'

/** The methods that carry the CSRF header. Cookie auth is ambient; a bearer header never was. */
export const STATE_CHANGING_METHODS: ReadonlySet<string> = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/**
 * Reads one cookie from `document.cookie`, or `null` when it is absent.
 *
 * Only ever used for `XSRF-TOKEN`. The session cookie is `HttpOnly` and invisible here, which is the
 * property this whole phase exists to buy.
 */
export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const prefix = `${encodeURIComponent(name)}=`
  for (const segment of document.cookie.split(';')) {
    const trimmed = segment.trim()
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length))
    }
  }
  return null
}

/**
 * Builds the sign-in kickoff URL.
 *
 * The `returnUrl` is made **origin-absolute** on purpose. The BFF redirects to it after the round trip
 * through the identity provider, from a request that started at `/signin-oidc`, and it validates the value
 * against its allow-list before honouring it — a relative path would be ambiguous to that check. The hash
 * route survives because the whole URL is carried, and this app routes on the hash.
 *
 * `tenant` is a *hint* about which directory to authenticate against, from the `/t/{slug}` entry point and
 * from the tenant switcher. It is never an authorization decision: the identity provider resolves it
 * against the signing-in user's own memberships and refuses one they do not belong to.
 */
export function buildLoginUrl(returnUrl: string, tenant?: string): string {
  const absolute = /^https?:\/\//i.test(returnUrl)
    ? returnUrl
    : `${window.location.origin}${returnUrl.startsWith('/') ? '' : '/'}${returnUrl}`

  const hint = tenant ? `&tenant=${encodeURIComponent(tenant)}` : ''
  return `${BFF_LOGIN_PATH}?returnUrl=${encodeURIComponent(absolute)}${hint}`
}

/** Where the browser is now, as something `buildLoginUrl` can send it back to. */
export function currentReturnUrl(): string {
  if (typeof window === 'undefined') {
    return '/'
  }
  return window.location.origin + window.location.pathname + window.location.search + window.location.hash
}

/** The `GET /bff/user` response — the curated claims slice the cookie already carries. No tokens. */
export interface BffUserResponse {
  readonly subject: string
  readonly principalType: string
  readonly email: string | null
  readonly displayName: string | null
  readonly roles: readonly string[]
  readonly tenantId: string | null
  readonly additionalClaims: Readonly<Record<string, string>>
}

/**
 * Narrows the parsed JSON to {@link BffUserResponse}, throwing when it is not that shape.
 *
 * A malformed body is an error rather than a signed-out answer. Coercing an unexpected shape into a
 * plausible user is how a session with no subject renders as somebody — the 401 path is the only thing
 * that should ever mean "not signed in".
 */
export function parseBffUser(json: unknown): BffUserResponse {
  if (typeof json !== 'object' || json === null) {
    throw new Error('/bff/user did not return an object')
  }

  const candidate = json as Record<string, unknown>
  if (typeof candidate.subject !== 'string' || candidate.subject.length === 0) {
    throw new Error('/bff/user returned no subject')
  }

  const roles = Array.isArray(candidate.roles)
    ? candidate.roles.filter((role): role is string => typeof role === 'string')
    : []

  const additionalClaims: Record<string, string> = {}
  if (typeof candidate.additionalClaims === 'object' && candidate.additionalClaims !== null) {
    for (const [key, value] of Object.entries(candidate.additionalClaims as Record<string, unknown>)) {
      if (typeof value === 'string') {
        additionalClaims[key] = value
      }
    }
  }

  return {
    subject: candidate.subject,
    principalType: typeof candidate.principalType === 'string' ? candidate.principalType : 'User',
    email: typeof candidate.email === 'string' ? candidate.email : null,
    displayName: typeof candidate.displayName === 'string' ? candidate.displayName : null,
    roles,
    tenantId: typeof candidate.tenantId === 'string' ? candidate.tenantId : null,
    additionalClaims,
  }
}
